var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import axios from "axios";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import client from "../http/client";
import GoogleAnalytics from "../analytics/google";
import AnalyticsManager from "../functions/analytics";
import { bugsnagClient } from "../themes/streamlined/streamlined";
var SPECIAL_ITEM_PAYLOAD_STORAGE_KEY = "ui-special-item-payloads";
var CartContext = createContext(undefined);
var isItemPayload = function (value) {
    return Boolean(value &&
        typeof value === "object" &&
        "item" in value &&
        "category" in value);
};
var getCachedSpecialItemPayload = function (itemNavigationId) {
    if (!itemNavigationId || typeof window === "undefined") {
        return null;
    }
    try {
        var cachedPayloads = window.sessionStorage.getItem(SPECIAL_ITEM_PAYLOAD_STORAGE_KEY);
        if (!cachedPayloads) {
            return null;
        }
        var parsedPayloads = JSON.parse(cachedPayloads);
        if (!parsedPayloads || typeof parsedPayloads !== "object") {
            return null;
        }
        var payload = parsedPayloads[String(itemNavigationId)];
        return isItemPayload(payload) ? payload : null;
    }
    catch (error) {
        var debugContext = {
            itemNavigationId: itemNavigationId,
            errorMessage: error instanceof Error ? error.message : String(error),
        };
        // eslint-disable-next-line no-console
        console.warn("[CartContext] Unable to read cached special item payload.", debugContext);
        bugsnagClient.leaveBreadcrumb("Unable to read cached special item payload.", debugContext);
        return null;
    }
};
var getChoiceDebugSummary = function (choices) {
    if (!choices) {
        return { hasChoices: false, choicesType: typeof choices, choiceKeys: [] };
    }
    if (typeof choices === "string") {
        return { hasChoices: choices.trim().length > 0, choicesType: "string", choiceKeys: [] };
    }
    if (typeof choices === "object") {
        return { hasChoices: true, choicesType: "object", choiceKeys: Object.keys(choices) };
    }
    return { hasChoices: true, choicesType: typeof choices, choiceKeys: [] };
};
var getCartLineDebugSummary = function (line) {
    var _a, _b, _c, _d, _e, _f, _g;
    return (__assign({ id: line.id, name: line.friendly_name || line.name || null, quantity: line.quantity, category_id: (_a = line.category_id) !== null && _a !== void 0 ? _a : null, item_id: (_b = line.item_id) !== null && _b !== void 0 ? _b : null, item_navigation_id: (_c = line.item_navigation_id) !== null && _c !== void 0 ? _c : null, related_id: (_d = line.related_id) !== null && _d !== void 0 ? _d : null, parent_line_id: (_e = line.parent_line_id) !== null && _e !== void 0 ? _e : null, is_addon: (_f = line.is_addon) !== null && _f !== void 0 ? _f : null, addon_id: (_g = line.addon_id) !== null && _g !== void 0 ? _g : null }, getChoiceDebugSummary(line.choices)));
};
var syncCartItems = function (currentCart, nextLines) {
    if (!currentCart) {
        return currentCart;
    }
    if (Array.isArray(currentCart.lines)) {
        return __assign(__assign({}, currentCart), { lines: nextLines });
    }
    if (Array.isArray(currentCart.cart_lines)) {
        return __assign(__assign({}, currentCart), { cart_lines: nextLines });
    }
    if (Array.isArray(currentCart.items)) {
        return __assign(__assign({}, currentCart), { items: nextLines });
    }
    return currentCart;
};
export var CartProvider = function (_a) {
    var _b = _a.initialItems, initialItems = _b === void 0 ? [] : _b, children = _a.children;
    var _c = useState(null), cart = _c[0], setCart = _c[1];
    var _d = useState(0), cartRevision = _d[0], setCartRevision = _d[1];
    var _e = useState(initialItems), items = _e[0], setItems = _e[1];
    var _f = useState(true), isLoading = _f[0], setIsLoading = _f[1];
    var _g = useState(null), editingItem = _g[0], setEditingItem = _g[1];
    var hasCompletedInitialCartLoadRef = useRef(false);
    var getCart = useCallback(function () {
        var args_1 = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args_1[_i] = arguments[_i];
        }
        return __awaiter(void 0, __spreadArray([], args_1, true), void 0, function (options) {
            var shouldShowLoading, response, nextCart, cartLines, normalizedItems, relatedCartLineSummaries, debugContext, relatedItemLinesMissingChoices, debugContext;
            var _a, _b;
            if (options === void 0) { options = {}; }
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        shouldShowLoading = typeof options.showLoading === "boolean"
                            ? options.showLoading
                            : !hasCompletedInitialCartLoadRef.current;
                        if (shouldShowLoading) {
                            setIsLoading(true);
                        }
                        return [4 /*yield*/, client.get("/cart/api").catch(function (error) {
                                bugsnagClient.notify(error, function (event) {
                                    var _a, _b, _c, _d, _e, _f;
                                    var method = (_b = (_a = error.config) === null || _a === void 0 ? void 0 : _a.method) === null || _b === void 0 ? void 0 : _b.toUpperCase();
                                    var url = (_c = error.config) === null || _c === void 0 ? void 0 : _c.url;
                                    var reason = 'unknown';
                                    if (error.code === 'ECONNABORTED') {
                                        reason = 'timeout';
                                    }
                                    else if ((_d = error.response) === null || _d === void 0 ? void 0 : _d.status) {
                                        reason = error.response.status;
                                    }
                                    event.groupingHash = "".concat(method, ":").concat(url, ":").concat(reason);
                                    event.addMetadata('axios', {
                                        method: method,
                                        url: url,
                                        status: (_e = error.response) === null || _e === void 0 ? void 0 : _e.status,
                                        timeout: (_f = error.config) === null || _f === void 0 ? void 0 : _f.timeout,
                                        error: error
                                    });
                                });
                            })];
                    case 1:
                        response = _c.sent();
                        nextCart = (_b = ((_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.cart)) !== null && _b !== void 0 ? _b : null;
                        hasCompletedInitialCartLoadRef.current = true;
                        setCart(nextCart);
                        setCartRevision(function (revision) { return revision + 1; });
                        if (!nextCart) {
                            setItems([]);
                            setIsLoading(false);
                            return [2 /*return*/];
                        }
                        cartLines = Array.isArray(nextCart.lines)
                            ? nextCart.lines
                            : Array.isArray(nextCart.cart_lines)
                                ? nextCart.cart_lines
                                : Array.isArray(nextCart.items)
                                    ? nextCart.items
                                    : [];
                        normalizedItems = cartLines.filter(function (line) {
                            return Boolean(line && typeof line === "object" && "id" in line && "quantity" in line);
                        });
                        relatedCartLineSummaries = normalizedItems
                            .filter(function (line) { return Boolean(line.related_id); })
                            .map(getCartLineDebugSummary);
                        if (relatedCartLineSummaries.length > 0) {
                            debugContext = { relatedCartLineSummaries: relatedCartLineSummaries };
                            // eslint-disable-next-line no-console
                            console.info("[CartContext] Related cart lines loaded from cart API.", debugContext);
                            bugsnagClient.leaveBreadcrumb("Related cart lines loaded from cart API.", debugContext);
                        }
                        relatedItemLinesMissingChoices = normalizedItems
                            .filter(function (line) { return Boolean(line.related_id) && Boolean(line.item_navigation_id) && !Boolean(line.addon_id) && !Boolean(line.is_addon) && !line.choices; })
                            .map(getCartLineDebugSummary);
                        if (relatedItemLinesMissingChoices.length > 0) {
                            debugContext = { relatedItemLinesMissingChoices: relatedItemLinesMissingChoices };
                            // eslint-disable-next-line no-console
                            console.warn("[CartContext] Related item cart lines are missing choices.", debugContext);
                            bugsnagClient.leaveBreadcrumb("Related item cart lines are missing choices.", debugContext);
                        }
                        setItems(normalizedItems);
                        setIsLoading(false);
                        return [2 /*return*/];
                }
            });
        });
    }, []);
    useEffect(function () {
        var isMounted = true;
        getCart().catch(function () {
            if (!isMounted) {
                return;
            }
            setCart(null);
            setItems([]);
            setIsLoading(false);
        });
        return function () {
            isMounted = false;
        };
    }, [getCart]);
    var repeatOrder = useCallback(function (order) { return __awaiter(void 0, void 0, void 0, function () {
        var orderLines, url, requests, results, failedResult, error, description, someItemsAdded, _error_1;
        var _a, _b, _c;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    orderLines = Array.isArray(order === null || order === void 0 ? void 0 : order.lines) ? order.lines : [];
                    if (orderLines.length === 0) {
                        return [2 /*return*/, { success: false, message: "This order has no items to repeat." }];
                    }
                    url = "/cart/add/api";
                    _d.label = 1;
                case 1:
                    _d.trys.push([1, 4, , 5]);
                    requests = orderLines
                        .filter(function (line) { return Boolean(line === null || line === void 0 ? void 0 : line.item_id) && Boolean(line === null || line === void 0 ? void 0 : line.item_navigation_id); })
                        .map(function (line) { return client.post(url, {
                        reorder: true,
                        item: line.item_id,
                        addon_id: line.addon_id,
                        itemNavigation: line.item_navigation_id,
                        quantity: line.quantity,
                        comment: line.comment,
                        category_id: 0,
                        modifiers: Array.isArray(line.modifications) ? line.modifications : [],
                        is_addon: line.is_addon,
                        addOns: [],
                    }); });
                    if (requests.length === 0) {
                        return [2 /*return*/, { success: false, message: "This order has no available items to repeat." }];
                    }
                    return [4 /*yield*/, Promise.allSettled(requests)];
                case 2:
                    results = _d.sent();
                    return [4 /*yield*/, getCart()];
                case 3:
                    _d.sent();
                    failedResult = results.find(function (result) { return result.status === "rejected"; });
                    if (failedResult && failedResult.status === "rejected") {
                        error = failedResult.reason;
                        description = axios.isAxiosError(error)
                            ? (_c = (_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.friendly) === null || _c === void 0 ? void 0 : _c.description
                            : undefined;
                        someItemsAdded = results.some(function (result) { return result.status === "fulfilled"; });
                        return [2 /*return*/, {
                                success: false,
                                message: "".concat(someItemsAdded ? "Some items were added to your cart, but others could not be added. " : "Unable to repeat order. ").concat(typeof description === "string" ? description : "Please review your cart and try again."),
                            }];
                    }
                    return [2 /*return*/, { success: true, message: "Order repeated successfully." }];
                case 4:
                    _error_1 = _d.sent();
                    return [2 /*return*/, { success: false, message: "Unable to repeat order. Please review your cart and try again." }];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [getCart]);
    var submitItemAttempt = useCallback(function (itemContext) { return __awaiter(void 0, void 0, void 0, function () {
        var result;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, Promise.resolve(itemContext.functions.processSubmitAttempt())];
                case 1:
                    result = _a.sent();
                    if (!(typeof result === "undefined")) return [3 /*break*/, 3];
                    return [4 /*yield*/, getCart()];
                case 2:
                    _a.sent();
                    return [2 /*return*/, undefined];
                case 3: return [4 /*yield*/, getCart()];
                case 4:
                    _a.sent();
                    return [2 /*return*/, result];
            }
        });
    }); }, [getCart]);
    var addItem = useCallback(function (_item, itemContext) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, submitItemAttempt(itemContext)];
        });
    }); }, [submitItemAttempt]);
    var stopEditingItem = useCallback(function () {
        setEditingItem(null);
        delete window.editLineID;
        delete window.editLineQty;
        delete window.editItemChoices;
        delete window.editLineComment;
    }, []);
    var getAllCartLines = useCallback(function () {
        if (items.length > 0) {
            return items;
        }
        if (!cart || typeof cart !== "object") {
            return [];
        }
        if (Array.isArray(cart.lines)) {
            return cart.lines;
        }
        if (Array.isArray(cart.cart_lines)) {
            return cart.cart_lines;
        }
        if (Array.isArray(cart.items)) {
            return cart.items;
        }
        return [];
    }, [cart, items]);
    var updateItem = useCallback(function (id, categoryId) { return __awaiter(void 0, void 0, void 0, function () {
        var line, selectedCategoryId, selectedItemId, choices, editDebugContext, parsedChoicesDebugContext, parseErrorDebugContext, missingChoicesDebugContext, initialItemPayload, cachedPayloadDebugContext, editWindow, title;
        var _a, _b, _c, _d, _e;
        return __generator(this, function (_f) {
            switch (_f.label) {
                case 0: return [4 /*yield*/, client.post("/cart/discounts/clear/api").catch(function (err) {
                        bugsnagClient.notify(err);
                    })];
                case 1:
                    _f.sent();
                    line = getAllCartLines().find(function (cartLine) { return String(cartLine.id) === String(id); });
                    selectedCategoryId = (_a = line === null || line === void 0 ? void 0 : line.category_id) !== null && _a !== void 0 ? _a : categoryId;
                    selectedItemId = (_b = line === null || line === void 0 ? void 0 : line.item_navigation_id) !== null && _b !== void 0 ? _b : line === null || line === void 0 ? void 0 : line.item_id;
                    if (!selectedCategoryId || !selectedItemId) {
                        stopEditingItem();
                        return [2 /*return*/];
                    }
                    choices = line === null || line === void 0 ? void 0 : line.choices;
                    editDebugContext = line ? { cartLine: getCartLineDebugSummary(line) } : { requestedLineId: id, lineFound: false };
                    // eslint-disable-next-line no-console
                    console.info("[CartContext] Preparing cart line for item edit.", editDebugContext);
                    bugsnagClient.leaveBreadcrumb("Preparing cart line for item edit.", editDebugContext);
                    if (typeof choices === "string") {
                        try {
                            choices = JSON.parse(choices);
                            parsedChoicesDebugContext = {
                                cartLineId: id,
                                parsedChoiceKeys: choices && typeof choices === "object" ? Object.keys(choices) : [],
                            };
                            // eslint-disable-next-line no-console
                            console.info("[CartContext] Parsed cart line choices for item edit.", parsedChoicesDebugContext);
                            bugsnagClient.leaveBreadcrumb("Parsed cart line choices for item edit.", parsedChoicesDebugContext);
                        }
                        catch (error) {
                            parseErrorDebugContext = {
                                cartLineId: id,
                                rawChoices: line === null || line === void 0 ? void 0 : line.choices,
                                errorMessage: error instanceof Error ? error.message : String(error),
                            };
                            // eslint-disable-next-line no-console
                            console.warn("[CartContext] Unable to parse cart line choices for item edit.", parseErrorDebugContext);
                            bugsnagClient.leaveBreadcrumb("Unable to parse cart line choices for item edit.", parseErrorDebugContext);
                            choices = null;
                        }
                    }
                    if (!choices) {
                        missingChoicesDebugContext = line ? { cartLine: getCartLineDebugSummary(line) } : { requestedLineId: id, lineFound: false };
                        // eslint-disable-next-line no-console
                        console.warn("[CartContext] Cart line edit is opening without choices.", missingChoicesDebugContext);
                        bugsnagClient.leaveBreadcrumb("Cart line edit is opening without choices.", missingChoicesDebugContext);
                    }
                    initialItemPayload = getCachedSpecialItemPayload(selectedItemId);
                    if (initialItemPayload) {
                        cachedPayloadDebugContext = {
                            cartLineId: id,
                            itemNavigationId: selectedItemId,
                            categoryId: (_c = initialItemPayload.category.id) !== null && _c !== void 0 ? _c : null,
                        };
                        // eslint-disable-next-line no-console
                        console.info("[CartContext] Using cached special item payload for cart line edit.", cachedPayloadDebugContext);
                        bugsnagClient.leaveBreadcrumb("Using cached special item payload for cart line edit.", cachedPayloadDebugContext);
                    }
                    editWindow = window;
                    editWindow.editLineID = id;
                    editWindow.editLineQty = (_d = line === null || line === void 0 ? void 0 : line.quantity) !== null && _d !== void 0 ? _d : 1;
                    editWindow.editItemChoices = choices ? JSON.stringify(choices) : "";
                    title = (line === null || line === void 0 ? void 0 : line.friendly_name) || (line === null || line === void 0 ? void 0 : line.name) || "Customize item";
                    GoogleAnalytics.viewItem({
                        id: selectedItemId,
                        name: title,
                        category: selectedCategoryId,
                    });
                    setEditingItem({
                        id: id,
                        categoryId: selectedCategoryId,
                        itemUrl: "".concat(window.relativeURI(), "/menu/categories/").concat(selectedCategoryId, "/items/").concat(selectedItemId),
                        title: title,
                        choices: choices,
                        initialItemPayload: initialItemPayload,
                        quantity: (_e = line === null || line === void 0 ? void 0 : line.quantity) !== null && _e !== void 0 ? _e : 1,
                    });
                    return [2 /*return*/];
            }
        });
    }); }, [getAllCartLines, stopEditingItem]);
    var submitEditItemAttempt = useCallback(function (itemContext) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, submitItemAttempt(itemContext)];
        });
    }); }, [submitItemAttempt]);
    var removeItem = useCallback(function (id) { return __awaiter(void 0, void 0, void 0, function () {
        var lineToRemove, removalSucceeded, error_1, status;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    lineToRemove = getAllCartLines().find(function (cartLine) { return String(cartLine.id) === String(id); });
                    removalSucceeded = false;
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, client.delete("/cart/lines/".concat(id, "/delete/api"))];
                case 2:
                    _b.sent();
                    removalSucceeded = true;
                    return [3 /*break*/, 4];
                case 3:
                    error_1 = _b.sent();
                    status = (_a = error_1.response) === null || _a === void 0 ? void 0 : _a.status;
                    // 422 means the cart line no longer exists. Safe to continue.
                    if (status !== 422) {
                        bugsnagClient.notify(error_1);
                    }
                    return [3 /*break*/, 4];
                case 4:
                    if (removalSucceeded && lineToRemove) {
                        try {
                            AnalyticsManager.removeItemFromCart(lineToRemove);
                        }
                        catch (error) {
                            // Analytics must never block cart cleanup after a successful delete.
                            console.warn('[CartContext] Unable to track remove-from-cart analytics.', error);
                        }
                    }
                    //Refresh after deleting the item.
                    return [4 /*yield*/, getCart()];
                case 5:
                    //Refresh after deleting the item.
                    _b.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [getAllCartLines, getCart]);
    var updateTip = useCallback(function (tipAmount) { return __awaiter(void 0, void 0, void 0, function () {
        var normalizedTipAmount;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    normalizedTipAmount = tipAmount !== null && tipAmount !== void 0 ? tipAmount : 0;
                    return [4 /*yield*/, client.post("/cart/setTipAmount/api", {
                            tip_amount: normalizedTipAmount,
                        })];
                case 1:
                    _a.sent();
                    return [4 /*yield*/, getCart({ showLoading: false })];
                case 2:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [getCart]);
    var clearCart = useCallback(function () {
        setItems([]);
        setCart(function (currentCart) { return syncCartItems(currentCart, []); });
        setCartRevision(function (revision) { return revision + 1; });
    }, []);
    var value = useMemo(function () { return ({
        cart: cart,
        cartRevision: cartRevision,
        items: items,
        isLoading: isLoading,
        getCart: getCart,
        repeatOrder: repeatOrder,
        submitItemAttempt: submitItemAttempt,
        addItem: addItem,
        updateItem: updateItem,
        editingItem: editingItem,
        stopEditingItem: stopEditingItem,
        submitEditItemAttempt: submitEditItemAttempt,
        removeItem: removeItem,
        updateTip: updateTip,
        clearCart: clearCart,
    }); }, [cart, cartRevision, items, isLoading, getCart, repeatOrder, submitItemAttempt, addItem, updateItem, editingItem, stopEditingItem, submitEditItemAttempt, removeItem, updateTip, clearCart]);
    return (React.createElement(CartContext.Provider, { value: value }, children));
};
export var useCart = function () {
    var context = useContext(CartContext);
    if (!context) {
        throw new Error("useCart must be used within a CartProvider");
    }
    return context;
};
